import { useEffect, useState } from "react";
import { apiClient, ApiError } from "../../lib/api-client";
import { formatDateSaoPaulo } from "../../lib/currency";

interface LicensePool {
  id: string;
  tenantId: string;
  tenantTradeName: string;
  tenantSlug: string;
  kind: string;
  totalLicenses: number;
  consumed: number;
  startsAt: string;
  endsAt: string;
  createdAt: string;
}

interface TenantOption {
  id: string;
  trade_name: string;
}

export function AdminLicensesPage() {
  const [pools, setPools] = useState<LicensePool[]>([]);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [form, setForm] = useState({
    tenantId: "",
    kind: "per_title" as "specific_titles" | "defined_catalog" | "per_title" | "global_quota",
    totalLicenses: 10,
    startsAt: new Date().toISOString().slice(0, 10),
    endsAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  });

  function refresh() {
    setLoading(true);
    Promise.all([
      apiClient.get<{ items: LicensePool[] }>("/admin/licenses"),
      apiClient.get<{ items: TenantOption[] }>("/admin/tenants"),
    ])
      .then(([poolsData, tenantsData]) => {
        setPools(poolsData.items);
        setTenants(tenantsData.items);
        if (tenantsData.items.length > 0) {
          setForm((prev) => (prev.tenantId ? prev : { ...prev, tenantId: tenantsData.items[0]?.id ?? "" }));
        }
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "Não foi possível carregar licenças."))
      .finally(() => setLoading(false));
  }

  useEffect(refresh, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);
    setError(null);
    try {
      await apiClient.post("/admin/licenses", {
        tenantId: form.tenantId,
        kind: form.kind,
        totalLicenses: Number(form.totalLicenses),
        startsAt: new Date(form.startsAt).toISOString(),
        endsAt: new Date(form.endsAt).toISOString(),
      });
      setMessage("Pool de licenças corporativas criado com sucesso.");
      refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao emitir licenças.");
    }
  }

  return (
    <div>
      <h2>Licenças Corporativas</h2>

      {message && <p role="status" className="notice-success">{message}</p>}
      {error && <p role="alert" className="notice-error">{error}</p>}

      <details className="card" style={{ marginBottom: "var(--space-6)" }}>
        <summary style={{ cursor: "pointer", fontWeight: 600 }}>+ Emitir novo pool de licenças para tenant</summary>
        <form onSubmit={handleCreate} style={{ marginTop: "var(--space-4)", maxWidth: 460 }}>
          <div className="field">
            <label htmlFor="tenantSelect">Empresa / Tenant</label>
            <select
              id="tenantSelect"
              required
              value={form.tenantId}
              onChange={(e) => setForm({ ...form, tenantId: e.target.value })}
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.trade_name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label htmlFor="poolKind">Modalidade de Licenciamento</label>
            <select
              id="poolKind"
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as typeof form.kind })}
            >
              <option value="per_title">Por Título / Obra</option>
              <option value="global_quota">Cota Global de Acessos</option>
              <option value="defined_catalog">Catálogo Específico Definido</option>
              <option value="specific_titles">Títulos Específicos</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor="totalLicenses">Quantidade Total de Licenças</label>
            <input
              id="totalLicenses"
              type="number"
              min="1"
              required
              value={form.totalLicenses}
              onChange={(e) => setForm({ ...form, totalLicenses: Number(e.target.value) })}
            />
          </div>

          <div className="field">
            <label htmlFor="startsAt">Início da Vigência</label>
            <input
              id="startsAt"
              type="date"
              required
              value={form.startsAt}
              onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
            />
          </div>

          <div className="field">
            <label htmlFor="endsAt">Término da Vigência</label>
            <input
              id="endsAt"
              type="date"
              required
              value={form.endsAt}
              onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
            />
          </div>

          <button className="btn" type="submit" style={{ marginTop: "var(--space-3)" }}>
            Emitir Pool de Licenças
          </button>
        </form>
      </details>

      <div className="card" style={{ overflowX: "auto" }}>
        {loading ? (
          <p className="text-muted">Carregando licenças corporativas...</p>
        ) : pools.length === 0 ? (
          <p className="text-muted">Nenhum pool de licenças corporativas emitido até o momento.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Modalidade</th>
                <th className="num">Capacidade</th>
                <th className="num">Consumidas</th>
                <th className="num">Restantes</th>
                <th>Vigência</th>
              </tr>
            </thead>
            <tbody>
              {pools.map((p) => {
                const remaining = Math.max(0, p.totalLicenses - p.consumed);
                const percent = Math.min(100, Math.round((p.consumed / p.totalLicenses) * 100));
                return (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.tenantTradeName}</strong>
                      <div className="text-muted" style={{ fontSize: "var(--text-xs)" }}>
                        /{p.tenantSlug}
                      </div>
                    </td>
                    <td>{p.kind}</td>
                    <td className="num">{p.totalLicenses}</td>
                    <td className="num">
                      {p.consumed}
                      <span className="text-muted" style={{ fontSize: "var(--text-xs)", marginLeft: "var(--space-1)" }}>
                        ({percent}%)
                      </span>
                    </td>
                    <td className="num">{remaining}</td>
                    <td>
                      {formatDateSaoPaulo(p.startsAt)} a {formatDateSaoPaulo(p.endsAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
